import { useEffect, useState } from "react";
import { listClients } from "../services";
import { clientLabelOf } from "../utils/clientLabel";
import { describeApiError } from "../utils/describeApiError";

const SERVICE_UNAVAILABLE_MESSAGE =
  "Serviço de consumo via API indisponível. Tente novamente mais tarde.";

const CLIENTS_UNAVAILABLE_MESSAGE =
  "Não foi possível carregar a lista de clientes.";

const EMPTY_FILTERS = { clientId: "", from: "", to: "" };

// Todo o comportamento das abas de consumo. As abas Cálculos e Consultas diferem
// só em duas constantes — a função de serviço e o mapa de labels — então elas são
// dois arquivos curtos em cima deste hook, não duas cópias.
export function useUsageTab({ fetchUsage, labels }) {
  const [clients, setClients] = useState([]);
  const [clientsError, setClientsError] = useState(null);

  // filtros digitados vs. filtros de fato buscados. Vazio = default de 30 dias
  // apurado pelo backend, e é por isso que a tela exibe o `period` da resposta
  // em vez do que o usuário digitou.
  const [formFilters, setFormFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);

  const [usage, setUsage] = useState(null);
  const [usageError, setUsageError] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  // A lista alimenta apenas o seletor.
  //
  // Falhar aqui degrada para "seletor só com Todos + consolidado", que é o estado
  // default e continua útil: NÃO escreve em `usageError` nem bloqueia a tela. Mas
  // também não pode ser silencioso — sem sinal, "a lista falhou" e "não há
  // clientes cadastrados" ficam indistinguíveis, e o seletor vazio parece bug da
  // tela. Daí `clientsError` separado, exibido junto ao seletor.
  useEffect(() => {
    let current = true;

    listClients()
      .then(loadedClients => {
        if (!current) return;
        setClients(loadedClients);
        setClientsError(null);
      })
      .catch(() => {
        if (!current) return;
        setClients([]);
        // Frase única, sem o status HTTP: "HTTP 404" não diz nada a quem está
        // olhando o relatório e faz parecer defeito da página. Também não passa
        // pelo `describeApiError` — o 404 dele é "cliente não encontrado", e aqui
        // 404 é a ROTA ausente (proxy não publicado), o que daria a frase errada.
        // O status continua no console do axios e no `x-trace-id`, para suporte.
        setClientsError(CLIENTS_UNAVAILABLE_MESSAGE);
      });

    return () => {
      current = false;
    };
  }, []);

  useEffect(() => {
    // o form segue clicável durante o fetch, então duas buscas podem estar em
    // voo — a que for descartada não escreve mais no estado
    let current = true;

    const loadUsage = async () => {
      setUsageLoading(true);
      setUsageError(null);

      try {
        const loadedUsage = await fetchUsage(appliedFilters);
        if (!current) return;
        setUsage(loadedUsage);
      } catch (error) {
        if (!current) return;
        // O 404 nunca chega aqui: `getUsage` já o converte em estado vazio
        // (RF-20). Sobram o 400, cuja `message` é acionável e vai crua para a
        // tela, e o 5xx, que é indisponibilidade de infraestrutura — nunca
        // sessão expirada, nem no 502 CALCULATOR_API_UNAUTHORIZED, então nada
        // de logout aqui.
        setUsageError(describeApiError(error, SERVICE_UNAVAILABLE_MESSAGE));
        setUsage(null);
      } finally {
        if (current) setUsageLoading(false);
      }
    };

    loadUsage();

    return () => {
      current = false;
    };
  }, [fetchUsage, appliedFilters]);


  // `byClient` só vem no consolidado (sem filtro de cliente); `client` só vem
  // quando há filtro. Nunca as duas.
  const byClientRows = (usage?.byClient ?? []).map(row => ({
    ...row,
    key: row.clientId,
    // `name: null` é linha órfã: consumo de um clientId que não corresponde a
    // nenhum cliente atual (o vínculo é lógico, sem FK). Exibida de propósito e
    // rotulada com o id — esconder daria um total que não fecha. Já `name`
    // preenchido com `backendClientId: null` é cliente que existe e só não está
    // vinculado a um cliente da Jusfy, e aparece com o nome e "sem vínculo".
    label: row.name
      ? clientLabelOf(row)
      : `cliente removido (${row.clientId})`,
  }));

  const breakdownRows = (usage?.breakdown ?? []).map(row => ({
    ...row,
    key: row.slug,
    // a lista de slugs cresce sem aviso no calculator-api, então slug
    // desconhecido cai no próprio slug em vez de quebrar a tela
    label: labels[row.slug] ?? row.slug,
  }));

  return {
    clients,
    clientsError,
    formFilters,
    // o que está de fato buscado — o seletor de cliente parte dele para não
    // arrastar um período digitado e não submetido para a busca
    appliedFilters,
    setFormFilters,
    // recebe os filtros a aplicar: o seletor de cliente aplica na hora, o
    // período só no submit
    applyFilters: setAppliedFilters,
    usage,
    usageError,
    usageLoading,
    byClientRows,
    breakdownRows,
  };
}
